import { useUser, useAuth } from '@clerk/clerk-react';
import { useMemo, useEffect, useState } from 'react';
import { 
  saveGridToFirestore,
  getUserGridsFromFirestore,
  getGridFromFirestore,
  getGridPreviewFromFirestore,
  deleteGridFromFirestore,
  saveUserSettingsToFirestore,
  getUserSettingsFromFirestore,
  cleanupOrphanedGrids,
  updateGridTitle,
  GridData,
  GridMetadata,
  TemplateData,
  getGeneralTemplatesFromFirestore,
  getUserTemplatesFromFirestore,
  saveUserTemplateToFirestore,
  deleteUserTemplateFromFirestore,
  getTemplatePreviewFromFirestore,
  getFullTemplateFromFirestore,
  saveGlobalTemplateToFirestore,
  deleteGlobalTemplateFromFirestore,
  saveCellChangesToFirestore,
  saveRowChangesToFirestore,
  saveBatchedChangesToFirestore,
  applyIncrementalGridChanges,
  createCellChange,
  createRowChange,
  CellChange,
  RowChange
} from './firestoreService';
import { signIntoFirebaseWithClerk } from './firebase';

// Cache management
interface CacheState {
  grids: GridMetadata[];
  gridData: Map<string, GridData>;
  lastUpdated: number;
}

interface TemplateCacheState {
  generalTemplates: TemplateData[];
  myTemplates: TemplateData[];
  templatePreviews: Map<string, GridData>;
  lastUpdated: number;
}

// Cache storage
const userCaches = new Map<string, CacheState>();
const templateCaches = new Map<string, TemplateCacheState>();

// Cache validity duration (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Get cache for user
const getUserCache = (userId: string): CacheState => {
  if (!userCaches.has(userId)) {
    userCaches.set(userId, {
      grids: [],
      gridData: new Map(),
      lastUpdated: 0
    });
  }
  return userCaches.get(userId)!;
};

// Check if cache is valid
const isCacheValid = (cache: CacheState): boolean => {
  return Date.now() - cache.lastUpdated < CACHE_DURATION;
};

// Update cache with new grids metadata
const updateGridsCache = (userId: string, grids: GridMetadata[]): void => {
  const cache = getUserCache(userId);
  
  // Deduplicate grids array by ID before caching
  const uniqueGrids = grids.filter((grid, index, arr) => 
    arr.findIndex(g => g.id === grid.id) === index
  );
  
  cache.grids = uniqueGrids;
  cache.lastUpdated = Date.now();
  
  // Also cache in localStorage for persistence across sessions
  try {
    localStorage.setItem(`gridCache_${userId}`, JSON.stringify({
      grids: uniqueGrids,
      lastUpdated: cache.lastUpdated
    }));
  } catch (e) {
    // Ignore localStorage errors
  }
};

// Load cache from localStorage on startup
const loadCacheFromStorage = (userId: string): void => {
  try {
    const cached = localStorage.getItem(`gridCache_${userId}`);
    if (cached) {
      const { grids, lastUpdated } = JSON.parse(cached);
      const cache = getUserCache(userId);
      cache.grids = grids;
      cache.lastUpdated = lastUpdated;
    }
  } catch (e) {
    // Ignore localStorage errors
  }
};

// Add grid to cache
const addGridToCache = (userId: string, grid: GridData): void => {
  const cache = getUserCache(userId);
  cache.gridData.set(grid.id, grid);
  
  // Update metadata cache as well
  const existingIndex = cache.grids.findIndex(g => g.id === grid.id);
  const metadata: GridMetadata = {
    id: grid.id,
    title: grid.title,
    created: grid.created,
    lastModified: grid.lastModified,
    userId: grid.userId
  };
  
  if (existingIndex >= 0) {
    // Update existing grid metadata
    cache.grids[existingIndex] = metadata;
  } else {
    // Add new grid metadata to the beginning
    cache.grids.unshift(metadata);
  }
  
  // Deduplicate grids array by ID (just in case)
  const uniqueGrids = cache.grids.filter((grid, index, arr) => 
    arr.findIndex(g => g.id === grid.id) === index
  );
  cache.grids = uniqueGrids;
  
  // Sort by lastModified
  cache.grids.sort((a, b) => b.lastModified - a.lastModified);
  cache.lastUpdated = Date.now();
  
  // Update localStorage
  updateGridsCache(userId, cache.grids);
};

// Remove grid from cache
const removeGridFromCache = (userId: string, gridId: string): void => {
  const cache = getUserCache(userId);
  cache.gridData.delete(gridId);
  cache.grids = cache.grids.filter(g => g.id !== gridId);
  cache.lastUpdated = Date.now();
  
  // Update localStorage
  updateGridsCache(userId, cache.grids);
};

// Interface for grid data (keeping backward compatibility)
export interface GridDataLegacy {
  rows: any[];
  columns: any[];
  title: string;
  lastModified: number;
}

/**
 * Get grid storage key for localStorage fallback
 */
const getStorageKey = (userId: string, key: string): string => {
  return `user_${userId}_${key}`;
};

/**
 * Generate a unique ID for new grids
 */
const generateGridId = (): string => {
  return `grid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create a new grid with default data
 */
export const createNewUserGrid = async (userId: string): Promise<GridData> => {
  // Default column names for new spreadsheets
  const defaultColumnNames = ["Name", "Company", "Work email", "Phone number"];
  
  const newGrid: GridData = {
    id: generateGridId(),
    title: `Untitled Sheet`,
    gridData: Array.from({ length: 50 }, (_, index) => ({ id: index + 1 })),
    columnConfigs: Array.from({ length: 10 }, (_, index) => ({
      id: index,
      name: index < defaultColumnNames.length ? defaultColumnNames[index] : '',
      options: {},
      width: 200,
    })),
    created: Date.now(),
    lastModified: Date.now(),
    userId
  };

  // Add to cache immediately (optimistic update)
  addGridToCache(userId, newGrid);

  try {
    await saveGridToFirestore(newGrid);
    return newGrid;
  } catch (error) {
    console.error('Failed to create new grid in Firestore, falling back to localStorage:', error);
    // Fallback to localStorage
    const storageKey = getStorageKey(userId, `grid_${newGrid.id}`);
    localStorage.setItem(storageKey, JSON.stringify(newGrid));
    return newGrid;
  }
};

/**
 * Save grid data for a specific user to Firestore
 */
export const saveUserGridData = async (userId: string, gridData: GridData): Promise<void> => {
  // Update cache immediately (optimistic update)
  addGridToCache(userId, gridData);

  try {
    await saveGridToFirestore(gridData);
  } catch (error) {
    console.error('Failed to save grid data to Firestore, falling back to localStorage:', error);
    // Fallback to localStorage
    const storageKey = getStorageKey(userId, `grid_${gridData.id}`);
    localStorage.setItem(storageKey, JSON.stringify(gridData));
  }
};

/**
 * Load grid data for a specific user from Firestore with caching
 */
export const loadUserGridData = async (userId: string, gridId: string): Promise<GridData | null> => {
  // Check cache first
  const cache = getUserCache(userId);
  if (cache.gridData.has(gridId)) {
    return cache.gridData.get(gridId)!;
  }

  try {
    const gridData = await getGridFromFirestore(gridId, userId);
    if (gridData) {
      // Add to cache
      cache.gridData.set(gridId, gridData);
      return gridData;
    }
  } catch (error) {
    console.error('Failed to load grid data from Firestore, trying localStorage:', error);
  }
  
  // Fallback to localStorage
  try {
    const storageKey = getStorageKey(userId, `grid_${gridId}`);
    const storedData = localStorage.getItem(storageKey);
    
    if (!storedData) {
      return null;
    }
    
    const gridData = JSON.parse(storedData);
    // Add to cache
    cache.gridData.set(gridId, gridData);
    return gridData;
  } catch (error) {
    console.error('Failed to load grid data from localStorage:', error);
    return null;
  }
};

/**
 * Load minimal grid data for preview (only first 8 rows and 5 columns)
 */
export const loadUserGridPreview = async (userId: string, gridId: string): Promise<GridData | null> => {
  try {
    const previewData = await getGridPreviewFromFirestore(gridId, userId);
    return previewData;
  } catch (error) {
    console.error('Failed to load grid preview from Firestore:', error);
    // Don't fallback to localStorage for previews - keep it lightweight
    return null;
  }
};

/**
 * Get all grids for a specific user with aggressive caching
 */
export const getUserGrids = async (userId: string): Promise<GridMetadata[]> => {
  // Load cache from localStorage on first access
  const cache = getUserCache(userId);
  if (cache.grids.length === 0) {
    loadCacheFromStorage(userId);
  }

  // Return cached data if valid
  if (isCacheValid(cache)) {
    return cache.grids;
  }

  try {
    // Only clean up orphaned grids on first load or when cache is very old
    const isVeryOld = Date.now() - cache.lastUpdated > 30 * 60 * 1000; // 30 minutes
    if (cache.grids.length === 0 || isVeryOld) {
      await cleanupOrphanedGrids(userId);
    }
    
    const grids = await getUserGridsFromFirestore(userId);
    
    // Deduplicate grids before caching (safety measure)
    const uniqueGrids = grids.filter((grid, index, arr) => 
      arr.findIndex(g => g.id === grid.id) === index
    );
    
    updateGridsCache(userId, uniqueGrids);
    return uniqueGrids;
  } catch (error) {
    console.error('Failed to load user grids from Firestore:', error);
    // Return cached data even if expired as fallback
    return cache.grids;
  }
};

/**
 * Delete a user's grid with cache invalidation
 */
export const deleteUserGrid = async (userId: string, gridId: string): Promise<void> => {
  // Remove from cache immediately (optimistic update)
  removeGridFromCache(userId, gridId);

  try {
    await deleteGridFromFirestore(gridId, userId);
  } catch (error) {
    console.error(`Failed to delete grid ${gridId} from Firestore:`, error);
    // Fallback to localStorage cleanup
    const storageKey = getStorageKey(userId, `grid_${gridId}`);
    localStorage.removeItem(storageKey);
    throw error; // Re-throw to ensure the caller knows deletion failed
  }
};

/**
 * Update the title of a specific grid with cache update
 */
export const updateUserGridTitle = async (userId: string, gridId: string, newTitle: string): Promise<void> => {
  // Update cache immediately
  const cache = getUserCache(userId);
  const gridIndex = cache.grids.findIndex(g => g.id === gridId);
  if (gridIndex >= 0) {
    cache.grids[gridIndex].title = newTitle;
    cache.grids[gridIndex].lastModified = Date.now();
    cache.lastUpdated = Date.now();
    updateGridsCache(userId, cache.grids);
  }

  if (cache.gridData.has(gridId)) {
    const gridData = cache.gridData.get(gridId)!;
    gridData.title = newTitle;
    gridData.lastModified = Date.now();
  }

  try {
    await updateGridTitle(gridId, userId, newTitle);
  } catch (error) {
    console.error(`Failed to update grid title for ${gridId}:`, error);
    throw error;
  }
};

// Clear cache for user (useful for logout)
export const clearUserCache = (userId: string): void => {
  userCaches.delete(userId);
  try {
    localStorage.removeItem(`gridCache_${userId}`);
  } catch (e) {
    // Ignore localStorage errors
  }
};

// Legacy localStorage functions (keeping for compatibility)
const saveUserTitle = (userId: string, title: string) => {
  localStorage.setItem(`user_${userId}_title`, title);
};

const loadUserTitle = (userId: string): string => {
  return localStorage.getItem(`user_${userId}_title`) || '';
};

const clearUserData = (userId: string) => {
  // Clear cache
  clearUserCache(userId);
  
  // Clear localStorage
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(`user_${userId}_`)) {
      localStorage.removeItem(key);
    }
  });
};

/**
 * Get the active grid ID for a user
 */
export const getActiveUserGridId = async (userId: string): Promise<string | null> => {
  try {
    const settings = await getUserSettingsFromFirestore(userId);
    return settings.activeGridId || null;
  } catch (error) {
    console.error('Failed to get active grid ID:', error);
    return null;
  }
};

/**
 * Set the active grid ID for a user
 */
export const setActiveUserGrid = async (userId: string, gridId: string): Promise<void> => {
  try {
    await saveUserSettingsToFirestore(userId, { activeGridId: gridId });
  } catch (error) {
    console.error('Failed to set active grid:', error);
  }
};

const getTemplateCache = (userId: string): TemplateCacheState => {
  if (!templateCaches.has(userId)) {
    templateCaches.set(userId, {
      generalTemplates: [],
      myTemplates: [],
      templatePreviews: new Map(),
      lastUpdated: 0
    });
  }
  return templateCaches.get(userId)!;
};

const isTemplateCacheValid = (cache: TemplateCacheState): boolean => {
  return Date.now() - cache.lastUpdated < CACHE_DURATION;
};

const clearTemplateCache = (userId: string): void => {
  templateCaches.delete(userId);
};

/**
 * Hook to provide user storage functionality with caching
 */
export const useUserStorage = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [isFirebaseReady, setIsFirebaseReady] = useState(false);

  const userId = user?.id || '';

  useEffect(() => {
    if (!userId) {
      setIsFirebaseReady(false);
      return;
    }

    const initializeFirebase = async () => {
      try {
        await signIntoFirebaseWithClerk(getToken);
        setIsFirebaseReady(true);
      } catch (error) {
        console.error('Failed to initialize Firebase:', error);
        setIsFirebaseReady(false);
      }
    };

    initializeFirebase();
  }, [userId, getToken]);

  const storageAPI = useMemo(() => {
    if (!userId) return null;
    
    return {
      // New Firestore-based functions with caching
      saveGrid: async (gridData: GridData) => {
        return await saveUserGridData(userId, gridData);
      },
      loadGrid: async (gridId: string) => {
        return await loadUserGridData(userId, gridId);
      },
      loadGridPreview: async (gridId: string) => {
        return await loadUserGridPreview(userId, gridId);
      },
      getAllGrids: async () => {
        return await getUserGrids(userId);
      },
      deleteGrid: async (gridId: string) => {
        return await deleteUserGrid(userId, gridId);
      },
      createNewGrid: async () => {
        return await createNewUserGrid(userId);
      },
      getActiveGridId: async () => {
        return await getActiveUserGridId(userId);
      },
      setActiveGrid: async (gridId: string) => {
        return await setActiveUserGrid(userId, gridId);
      },
      updateTitle: async (gridId: string, newTitle: string) => {
        try {
          if (!userId) throw new Error('User not authenticated');
          
          await updateGridTitle(gridId, userId, newTitle);
          
          // Update cache
          const cache = getUserCache(userId);
          const gridMetadata = cache.grids.find(g => g.id === gridId);
          if (gridMetadata) {
            gridMetadata.title = newTitle;
            gridMetadata.lastModified = Date.now();
          }
          
          // Update grid data cache if present
          const gridData = cache.gridData.get(gridId);
          if (gridData) {
            gridData.title = newTitle;
            gridData.lastModified = Date.now();
          }
          
        } catch (error) {
          console.error('Failed to update grid title:', error);
          throw error;
        }
      },
      cleanupOrphanedGrids: async () => {
        return await cleanupOrphanedGrids(userId);
      },
      clearCache: () => {
        clearUserCache(userId);
      },
      
      // Incremental update functions
      saveCellChanges: async (gridId: string, cellChanges: CellChange[]) => {
        return await saveCellChangesToFirestore(gridId, userId, cellChanges);
      },
      saveRowChanges: async (gridId: string, rowChanges: RowChange[]) => {
        return await saveRowChangesToFirestore(gridId, userId, rowChanges);
      },
      saveBatchedChanges: async (gridId: string, cellChanges?: CellChange[], rowChanges?: RowChange[], columnConfigChanges?: any[]) => {
        return await saveBatchedChangesToFirestore(gridId, userId, cellChanges, rowChanges, columnConfigChanges);
      },
      applyIncrementalChanges: async (gridId: string, changes: {
        cellChanges?: CellChange[];
        rowChanges?: RowChange[];
        columnConfigChanges?: any[];
        title?: string;
      }) => {
        return await applyIncrementalGridChanges(gridId, userId, changes);
      },
      createCellChange,
      createRowChange,
      
      // Legacy functions for backward compatibility
      saveGridData: (data: GridDataLegacy) => {
        // Convert legacy format to new format
        const gridData: GridData = {
          id: generateGridId(),
          title: data.title,
          gridData: data.rows,
          columnConfigs: data.columns,
          created: Date.now(),
          lastModified: data.lastModified,
          userId
        };
        return saveUserGridData(userId, gridData);
      },
      loadGridData: () => {
        // This is deprecated - use loadGrid instead
        console.warn('loadGridData is deprecated, use loadGrid instead');
        return null;
      },
      saveTitle: (title: string) => saveUserTitle(userId, title),
      loadTitle: () => loadUserTitle(userId),
      clearData: () => clearUserData(userId),
      
      // Template-related functions
      getGeneralTemplates: async (): Promise<TemplateData[]> => {
        try {
          const cache = getTemplateCache(userId || '');
          
          // Return cached data if valid
          if (isTemplateCacheValid(cache) && cache.generalTemplates.length > 0) {
            return cache.generalTemplates;
          }
          
          const templates = await getGeneralTemplatesFromFirestore();
          
          // Update cache
          if (userId) {
            cache.generalTemplates = templates;
            cache.lastUpdated = Date.now();
          }
          
          return templates;
        } catch (error) {
          console.error('Failed to get general templates:', error);
          return [];
        }
      },

      getMyTemplates: async (): Promise<TemplateData[]> => {
        try {
          if (!userId) throw new Error('User not authenticated');
          
          const cache = getTemplateCache(userId);
          
          // Return cached data if valid
          if (isTemplateCacheValid(cache) && cache.myTemplates.length > 0) {
            return cache.myTemplates;
          }
          
          const templates = await getUserTemplatesFromFirestore(userId);
          
          // Update cache
          cache.myTemplates = templates;
          cache.lastUpdated = Date.now();
          
          return templates;
        } catch (error) {
          console.error('Failed to get user templates:', error);
          return [];
        }
      },

      saveToMyTemplates: async (gridData: GridData, description?: string, category?: string): Promise<void> => {
        try {
          if (!userId) throw new Error('User not authenticated');
          
          // Create template data from grid data
          const templateData: TemplateData = {
            ...gridData,
            id: `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            description: description || '',
            category: category || 'Personal',
            isPublic: false,
            userId: userId
          };
          
          await saveUserTemplateToFirestore(templateData, userId);
          
          // Clear cache to force refresh
          clearTemplateCache(userId);
          
        } catch (error) {
          console.error('Failed to save grid as template:', error);
          throw error;
        }
      },

      deleteMyTemplate: async (templateId: string): Promise<void> => {
        try {
          if (!userId) throw new Error('User not authenticated');
          await deleteUserTemplateFromFirestore(templateId, userId);
          
          // Clear cache to force refresh
          clearTemplateCache(userId);
        } catch (error) {
          console.error('Failed to delete template:', error);
          throw error;
        }
      },

      loadTemplatePreview: async (templateId: string, type: 'general' | 'personal'): Promise<GridData | null> => {
        try {
          const cache = getTemplateCache(userId || '');
          
          // Check cache first
          if (cache.templatePreviews.has(templateId)) {
            return cache.templatePreviews.get(templateId)!;
          }
          
          const userId_param = type === 'personal' ? userId : undefined;
          const preview = await getTemplatePreviewFromFirestore(templateId, type, userId_param);
          
          // Cache the preview
          if (preview && userId) {
            cache.templatePreviews.set(templateId, preview);
          }
          
          return preview;
        } catch (error) {
          console.error('Failed to load template preview:', error);
          return null;
        }
      },

      createGridFromTemplate: async (templateId: string, type: 'general' | 'personal'): Promise<GridData> => {
        try {
          if (!userId) throw new Error('User not authenticated');
          
          const userId_param = type === 'personal' ? userId : undefined;
          const template = await getFullTemplateFromFirestore(templateId, type, userId_param);
          
          if (!template) {
            throw new Error('Template not found');
          }
          
          // Create new grid from template
          const newGridId = `grid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const newGrid: GridData = {
            id: newGridId,
            title: `${template.title} (Copy)`,
            gridData: template.gridData || [],
            columnConfigs: template.columnConfigs || [],
            created: Date.now(),
            lastModified: Date.now(),
            userId: userId
          };
          
          // Save the new grid
          await saveGridToFirestore(newGrid);
          
          // Update cache
          const cache = getUserCache(userId);
          const metadata: GridMetadata = {
            id: newGrid.id,
            title: newGrid.title,
            created: newGrid.created,
            lastModified: newGrid.lastModified,
            userId: newGrid.userId
          };
          
          cache.grids.unshift(metadata);
          cache.gridData.set(newGrid.id, newGrid);
          
          return newGrid;
        } catch (error) {
          console.error('Failed to create grid from template:', error);
          throw error;
        }
      },

      saveToGlobalTemplates: async (gridData: GridData, description?: string, category?: string): Promise<void> => {
        try {
          if (!userId) throw new Error('User not authenticated');
          
          // Create template data from grid data
          const templateData: TemplateData = {
            ...gridData,
            id: `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            description: description || '',
            category: category || 'General',
            isPublic: true,
            userId: userId
          };
          
          await saveGlobalTemplateToFirestore(templateData);
          
          // Clear cache to force refresh
          clearTemplateCache(userId);
          
        } catch (error) {
          console.error('Failed to save grid as global template:', error);
          throw error;
        }
      },

      deleteGlobalTemplate: async (templateId: string): Promise<void> => {
        try {
          if (!userId) throw new Error('User not authenticated');
          await deleteGlobalTemplateFromFirestore(templateId);
          
          // Clear cache to force refresh
          clearTemplateCache(userId);
        } catch (error) {
          console.error('Failed to delete global template:', error);
          throw error;
        }
      },

      // Clear template cache when needed
      clearTemplateCache: () => {
        if (userId) {
          clearTemplateCache(userId);
        }
      },

      // User ID for direct access
      userId: userId,
      
      // Mark Firebase as ready
      isFirebaseReady
    };
  }, [userId, isFirebaseReady]);

  return storageAPI;
};